// src/services/adminApi.js - פתרון סופי עם Cloud Function
import { auth } from './firebase/firebaseConfig';

const ADMIN_API_CONFIG = {
  baseUrl: 'https://us-central1-savings-comparison-system.cloudfunctions.net',
  timeout: 30000
};

class AdminAPI {
  constructor() {
    this.baseUrl = ADMIN_API_CONFIG.baseUrl;
  }

  /**
   * 📊 קבלת נתונים מ-Cloud Function (עוקף CORS לחלוטין)
   */
  async getDataFromStorage(source, fileName = 'latest-data.json') {
    try {
     
      // בדיקה שהמשתמש מחובר
      const user = auth.currentUser;
      if (!user) {
        throw new Error('משתמש לא מחובר - נדרש אימות');
      }
      
    
      
      // קריאה ל-Cloud Function שלך לקבלת נתונים מ-Storage
      const response = await fetch(`${this.baseUrl}/getGamalNetData`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'שגיאה בקבלת נתונים');
      }

      // הנתונים יגיעו בפורמט שונה בהתאם למקור
      let data;
      if (source === 'gamal-net') {
        // אם זה בקשה כללית, נקבל metadata
        // אם זה בקשה ספציפית, נצטרך להוסיף פרמטר
        data = await this.getSpecificSourceData(source);
      } else {
        // למקורות אחרים, נצטרך להוסיף support ב-Cloud Function
        data = await this.getSpecificSourceData(source);
      }

      return data;
      
    } catch (error) {
      console.error(`❌ Error loading data via Cloud Function ${source}:`, error);
      
      if (error.message.includes('401') || error.message.includes('403')) {
        throw new Error('אין הרשאה לגישה - בדוק אימות המשתמש');
      } else if (error.message.includes('404')) {
        throw new Error(`נתונים לא נמצאו עבור ${source}`);
      } else if (error.message.includes('timeout')) {
        throw new Error('זמן הקריאה פג - נסה שוב');
      } else {
        throw new Error(`שגיאה בקבלת נתונים: ${error.message}`);
      }
    }
  }

  /**
   * 📊 קבלת נתונים ספציפיים למקור מסוים
   */
  async getSpecificSourceData(source) {
    try {
      // מיפוי למקורות הנתונים שלך
      const sourceMapping = {
        'gamal-net': 'gamal-net',
        'pension-net': 'pension-net', 
        'insurance-net': 'insurance-net'
      };

      const mappedSource = sourceMapping[source];
      if (!mappedSource) {
        throw new Error(`מקור לא ידוע: ${source}`);
      }

      // קריאה ישירה לקבלת כל הנתונים המעובדים
      const response = await fetch(`${this.baseUrl}/getStorageData?source=${mappedSource}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json'
        },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        // אם אין endpoint כזה, ננסה דרך הפונקציה הקיימת
        console.warn(`⚠️ getStorageData not available for ${mappedSource}, using fallback`);
        return await this.fallbackToExistingFunction(mappedSource);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'שגיאה בקבלת נתונים');
      }

      // החזרת הנתונים בפורמט המצופה
      return {
        cleanData: result.data,
        processedAt: result.metadata.processedAt,
        source: source,
        totalRecords: result.metadata.totalRecords,
        lastUpdate: result.lastUpdate,
        newFeatures: result.metadata.newFeatures
      };

    } catch (error) {
      console.warn(`⚠️ Specific source fetch failed, trying fallback: ${error.message}`);
      return await this.fallbackToExistingFunction(source);
    }
  }

  /**
   * 🔄 Fallback לפונקציה הקיימת שלך
   */
  async fallbackToExistingFunction(source) {
    try {
      // נשתמש בפונקציות הקיימות שלך לפי המקור
      let endpoint;
      
      switch (source) {
        case 'gamal-net':
          // נוכל להשתמש ב-getGamalNetData עם פרמטר category
          const allCategories = await Promise.all([
            this.getCategoryData('study-funds'),
            this.getCategoryData('pension-funds'),
            this.getCategoryData('investment-funds'),
            this.getCategoryData('child-savings')
          ]);
          
          // איחוד כל הקטגוריות לנתונים מלאים
          const allData = allCategories.reduce((acc, category) => {
            if (category.success && category.data) {
              acc.push(...category.data);
            }
            return acc;
          }, []);

          return {
            cleanData: allData,
            processedAt: new Date().toISOString(),
            source: source,
            totalRecords: allData.length
          };
          
        case 'pension-net':
          return await this.getCategoryData('pension-plans');
          
        case 'insurance-net':
          return await this.getCategoryData('insurance-savings');
          
        default:
          throw new Error(`לא ניתן לטעון נתונים עבור ${source}`);
      }

    } catch (error) {
      console.error(`❌ Fallback failed for ${source}:`, error);
      throw error;
    }
  }

  /**
   * 📂 קבלת נתוני קטגוריה ספציפית
   */
  async getCategoryData(category) {
    try {
      const response = await fetch(`${this.baseUrl}/getGamalNetData?category=${category}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error(`❌ Error getting category ${category}:`, error);
      throw error;
    }
  }

  /**
   * 🎯 קבלת פרטי קרן ספציפית מהנתונים השמורים
   */
  async getFundById(fundId, sourceHint = null) {
    try {
      // רשימת המקורות לחיפוש
      const sources = sourceHint ? [sourceHint] : ['gamal-net', 'pension-net', 'insurance-net'];
      
      for (const source of sources) {
        try {
          // קריאת הנתונים מהמקור
          const data = await this.getDataFromStorage(source);
          
          if (!data.cleanData || !Array.isArray(data.cleanData)) {
            console.warn(`⚠️ No clean data found in ${source}`);
            continue;
          }
          
          // חיפוש הקרן במקור הזה
          const fund = data.cleanData.find(f => {
            const fId = f.id;
            return fId == fundId || 
                   fId === fundId || 
                   fId === parseInt(fundId) || 
                   fId?.toString() === fundId?.toString();
          });
          
          if (fund) {
            // הוספת מידע על המקור
            const enrichedFund = {
              ...fund,
              _source: source,
              _sourceLabel: this.getSourceLabel(source),
              _loadedFrom: 'storage',
              _loadedAt: new Date().toISOString()
            };
            
            return {
              success: true,
              fund: enrichedFund,
              source: source,
              message: `קרן ${fundId} נמצאה ב-${this.getSourceLabel(source)}`
            };
          }
          
        } catch (sourceError) {
          console.warn(`⚠️ Error searching in ${source}:`, sourceError.message);
          continue;
        }
      }
      
      // אם לא נמצא בשום מקור
      return {
        success: false,
        fund: null,
        error: `קרן ${fundId} לא נמצאה בנתונים השמורים`,
        searchedSources: sources
      };
      
    } catch (error) {
      console.error(`❌ Admin: Error in getFundById:`, error);
      throw new Error(`שגיאה בחיפוש קרן ${fundId}: ${error.message}`);
    }
  }

  /**
   * 🔍 חיפוש קרנות מרובות לפי רשימת מזהים
   */
  async getFundsByIds(fundIds, sourceHint = null) {
    try {
      const results = [];
      const notFound = [];
      
      for (const fundId of fundIds) {
        try {
          const result = await this.getFundById(fundId, sourceHint);
          if (result.success) {
            results.push(result.fund);
          } else {
            notFound.push(fundId);
          }
        } catch (error) {
          console.warn(`⚠️ Error loading fund ${fundId}:`, error.message);
          notFound.push(fundId);
        }
      }
      
      return {
        success: true,
        funds: results,
        found: results.length,
        notFound: notFound,
        total: fundIds.length
      };
      
    } catch (error) {
      console.error(`❌ Admin: Error in getFundsByIds:`, error);
      throw error;
    }
  }

  /**
   * 📊 בדיקה האם קרן קיימת (ללא טעינת כל הנתונים)
   */
  async fundExists(fundId, sourceHint = null) {
    try {
      const result = await this.getFundById(fundId, sourceHint);
      return {
        exists: result.success,
        source: result.source || null,
        fundId: fundId
      };
    } catch (error) {
      return {
        exists: false,
        source: null,
        fundId: fundId,
        error: error.message
      };
    }
  }

  /**
   * 📈 קבלת סטטיסטיקות על קרן (מיקום יחסי, דירוג וכו')
   */
  async getFundStatistics(fundId, sourceHint = null) {
    try {
      const fundResult = await this.getFundById(fundId, sourceHint);
      
      if (!fundResult.success) {
        throw new Error(fundResult.error);
      }
      
      const fund = fundResult.fund;
      const source = fundResult.source;
      
      // טעינת כל הקרנות מאותו מקור לצורך השוואה
      const allFundsData = await this.getDataFromStorage(source);
      const allFunds = allFundsData.cleanData || [];
      
      // חישוב דירוגים ואחוזונים
      const stats = this.calculateFundRankings(fund, allFunds);
      
      return {
        success: true,
        fund: fund,
        statistics: stats,
        comparedTo: {
          source: source,
          totalFunds: allFunds.length
        }
      };
      
    } catch (error) {
      console.error(`❌ Error calculating fund statistics:`, error);
      throw error;
    }
  }

  /**
   * 📊 חישוב דירוגים ואחוזונים לקרן
   */
  calculateFundRankings(fund, allFunds) {
    const stats = {
      rankings: {},
      percentiles: {},
      compared: allFunds.length
    };
    
    // רשימת המדדים לחישוב
    const metrics = [
      { key: 'month1', path: 'returns.month1', name: 'תשואה חודשית' },
      { key: 'year1', path: 'returns.year1', name: 'תשואה שנתית' },
      { key: 'year3', path: 'returns.year3', name: 'תשואה 3 שנים' },
      { key: 'year5', path: 'returns.year5', name: 'תשואה 5 שנים' },
      { key: 'managementFee', path: 'fees.management', name: 'דמי ניהול', reverse: true }
    ];
    
    metrics.forEach(metric => {
      const fundValue = this.getNestedValue(fund, metric.path);
      
      if (fundValue !== null && fundValue !== undefined && !isNaN(fundValue)) {
        // יצירת רשימה ממוינת של כל הערכים
        const allValues = allFunds
          .map(f => this.getNestedValue(f, metric.path))
          .filter(v => v !== null && v !== undefined && !isNaN(v))
          .sort((a, b) => metric.reverse ? a - b : b - a); // הפוך לדמי ניהול
        
        if (allValues.length > 0) {
          const rank = allValues.findIndex(v => 
            metric.reverse ? v >= fundValue : v <= fundValue
          ) + 1;
          
          const percentile = Math.round((rank / allValues.length) * 100);
          
          stats.rankings[metric.key] = {
            rank: rank,
            total: allValues.length,
            value: fundValue,
            name: metric.name
          };
          
          stats.percentiles[metric.key] = {
            percentile: percentile,
            value: fundValue,
            name: metric.name
          };
        }
      }
    });
    
    return stats;
  }

  /**
   * 🔧 פונקציה עזר לקבלת ערך מנתיב מקונן
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  }

  /**
   * 📊 קבלת מטאדטה של כל המקורות
   */
  async getAllSourcesMetadata() {
    try {
      const sources = [];
      const sourceConfigs = [
        { key: 'gamal-net', name: 'גמל-נט', description: 'קופות גמל וקרנות השתלמות', bgColor: 'bg-blue-500' },
        { key: 'pension-net', name: 'פנסיה-נט', description: 'קרנות פנסיה', bgColor: 'bg-green-500' },
        { key: 'insurance-net', name: 'ביטוח-נט', description: 'פוליסות חיסכון', bgColor: 'bg-orange-500' }
      ];
      
      let totalRecords = 0;
      
      for (const sourceConfig of sourceConfigs) {
        try {
          const data = await this.getDataFromStorage(sourceConfig.key);
          
          const source = {
            ...sourceConfig,
            totalFunds: data.cleanData?.length || data.totalRecords || 0,
            lastUpdate: this.formatRelativeTime(data.processedAt || data.lastUpdate),
            healthy: true
          };
          
          sources.push(source);
          totalRecords += source.totalFunds;
          
        } catch (error) {
          console.warn(`⚠️ Could not load ${sourceConfig.key}:`, error.message);
          
          sources.push({
            ...sourceConfig,
            totalFunds: 0,
            lastUpdate: 'לא זמין',
            healthy: false,
            error: error.message
          });
        }
      }
      
      return {
        sources,
        totalRecords,
        lastGlobalUpdate: new Date().toISOString(),
        summary: {
          totalFunds: totalRecords,
          activeSources: sources.filter(s => s.healthy).length,
          categories: sources.filter(s => s.healthy).length
        }
      };

    } catch (error) {
      console.error('❌ Admin: Error loading sources metadata:', error);
      throw new Error(`שגיאה בטעינת מטאדטה: ${error.message}`);
    }
  }

  /**
   * 🔍 טעינת כל הקרנות ממקור ספציפי
   */
  async getAllFundsFromSource(sourceKey) {
    try {
      const data = await this.getDataFromStorage(sourceKey);
      
      if (!data.cleanData || !Array.isArray(data.cleanData)) {
        throw new Error('נתונים לא תקינים - חסר cleanData או שהוא לא מערך');
      }

      // הוספת מידע על המקור לכל קרן
      const allFunds = data.cleanData.map(fund => ({
        ...fund,
        source: sourceKey,
        sourceLabel: this.getSourceLabel(sourceKey)
      }));

      return allFunds;

    } catch (error) {
      console.error(`❌ Admin: Error loading ${sourceKey} via Cloud Function:`, error);
      throw new Error(`שגיאה בטעינת נתוני ${sourceKey}: ${error.message}`);
    }
  }

  /**
   * 🔍 חיפוש מתקדם - דרך Cloud Function או מקומי
   */
  async searchFundsAdvanced(query, options = {}) {
    try {
    
      
      // אם זה בקשה לכל הקרנות ממקור ספציפי
      if ((!query || query === '*' || query === '') && options.source && options.source !== 'all') {
     
        const allFunds = await this.getAllFundsFromSource(options.source);
        return {
          results: allFunds.slice(0, options.limit || 1000),
          totalFound: allFunds.length,
          searchedSources: [options.source],
          query: query
        };
      }

      // חיפוש רגיל דרך ה-Cloud Function שלך
      const searchParams = new URLSearchParams({
        query: query.trim(),
        limit: options.limit || 50
      });

      if (options.source && options.source !== 'all') {
        searchParams.append('source', options.source);
      }

      const response = await fetch(`${this.baseUrl}/searchAllSources?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'שגיאה בחיפוש');
      }

      const processedResults = (result.results || []).map(fund => this.transformFundForAdmin(fund));
      
    
      return {
        results: processedResults,
        totalFound: result.totalFound || processedResults.length,
        searchedSources: result.searchedSources || [],
        query: query
      };

    } catch (error) {
      console.error('❌ Admin: Search error:', error);
      throw new Error(`שגיאה בחיפוש: ${error.message}`);
    }
  }

  /**
   * 🔄 עדכון מקור נתונים ספציפי
   */
  async refreshDataSource(sourceKey) {
    try {
     
      const endpoints = {
        'gamal-net': '/fetchGamalNetData',
        'pension-net': '/fetchPensionNetData',
        'insurance-net': '/fetchInsuranceNetData'
      };

      const endpoint = endpoints[sourceKey];
      if (!endpoint) {
        throw new Error(`מקור נתונים לא ידוע: ${sourceKey}`);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'שגיאה בעדכון');
      }

   
      return {
        success: true,
        source: sourceKey,
        message: result.message,
        stats: result.stats,
        lastUpdate: new Date().toISOString()
      };

    } catch (error) {
      console.error(`❌ Admin: Error refreshing ${sourceKey}:`, error);
      throw new Error(`שגיאה בעדכון ${sourceKey}: ${error.message}`);
    }
  }

  /**
   * 🔄 עדכון כל המקורות
   */
  async refreshAllSources() {
    try {
    
      const response = await fetch(`${this.baseUrl}/fetchAllData`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: ADMIN_API_CONFIG.timeout
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      
   
      return {
        success: true,
        results: result,
        completedAt: new Date().toISOString()
      };

    } catch (error) {
      console.error('❌ Admin: Error refreshing all sources:', error);
      throw new Error(`שגיאה בעדכון כל המקורות: ${error.message}`);
    }
  }

  /**
   * 🏷️ קבלת תווית מקור
   */
  getSourceLabel(sourceKey) {
    const labels = {
      'gamal-net': 'גמל-נט',
      'pension-net': 'פנסיה-נט',
      'insurance-net': 'ביטוח-נט'
    };
    return labels[sourceKey] || sourceKey;
  }

  /**
   * 🔄 המרת קרן לפורמט מנהל
   */
  transformFundForAdmin(rawFund) {
    const safeParseFloat = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    // בדיקה אם זה נתונים מעובדים או גולמיים
    const isProcessed = rawFund.hasOwnProperty('returns') && 
                       rawFund.hasOwnProperty('performance') &&
                       typeof rawFund.returns === 'object';

    if (isProcessed) {
      // נתונים מעובדים מ-Storage
      return {
        id: rawFund.id?.toString() || '',
        name: rawFund.name || 'לא זמין',
        company: rawFund.company || 'לא זמין',
        classification: rawFund.classification || '',
        
        source: rawFund.source || rawFund._source || 'unknown',
        sourceLabel: rawFund.sourceLabel || rawFund._sourceLabel || 'לא ידוע',
        
        returns: {
          month1: safeParseFloat(rawFund.returns?.month1),
          ytd: safeParseFloat(rawFund.returns?.ytd),
          year1: safeParseFloat(rawFund.returns?.year1),
          year3: safeParseFloat(rawFund.returns?.year3),
          year5: safeParseFloat(rawFund.returns?.year5),
          avgAnnual3Years: safeParseFloat(rawFund.returns?.avgAnnual3Years),
          avgAnnual5Years: safeParseFloat(rawFund.returns?.avgAnnual5Years)
        },
        
        fees: {
          management: safeParseFloat(rawFund.fees?.management),
          deposit: safeParseFloat(rawFund.fees?.deposit)
        },
        
        performance: {
          standardDeviation: safeParseFloat(rawFund.performance?.standardDeviation),
          alpha: safeParseFloat(rawFund.performance?.alpha),
          sharpeRatio: safeParseFloat(rawFund.performance?.sharpeRatio),
          liquidAssetsPercent: safeParseFloat(rawFund.performance?.liquidAssetsPercent)
        },
        
        financialData: {
          totalAssets: rawFund.totalAssets || 0,
          deposits: rawFund.deposits || 0,
          withdrawals: rawFund.withdrawals || 0,
          netMonthlyDeposits: rawFund.netMonthlyDeposits || 0
        },
        
        exposures: {
          stocks: safeParseFloat(rawFund.exposures?.stocks),
          foreign: safeParseFloat(rawFund.exposures?.foreign),
          currency: safeParseFloat(rawFund.exposures?.currency)
        },
        
        fundInfo: {
          inceptionDate: rawFund.inceptionDate || null,
          targetPopulation: rawFund.targetPopulation || 'כלל האוכלוסיה',
          specialization: rawFund.specialization || '',
          subSpecialization: rawFund.subSpecialization || '',
          controllingCorp: rawFund.controllingCorp || '',
          managingCorp: rawFund.managingCorp || ''
        },
        
        technical: {
          reportPeriod: rawFund.reportPeriod?.toString() || '',
          lastUpdate: rawFund.lastUpdate || new Date().toISOString(),
          managingCorpLegalId: rawFund.managingCorpLegalId || ''
        }
      };
      
    } else {
      // נתונים גולמיים מ-API
      return {
        id: rawFund.FUND_ID?.toString() || '',
        name: rawFund.FUND_NAME || 'לא זמין',
        company: rawFund.PARENT_COMPANY_NAME || 
                 rawFund.MANAGING_CORPORATION || 
                 rawFund.CONTROLLING_CORPORATION || 'לא זמין',
        classification: rawFund.FUND_CLASSIFICATION || '',
        
        source: rawFund._source || 'unknown',
        sourceLabel: rawFund._sourceLabel || 'לא ידוע',
        
        returns: {
          month1: safeParseFloat(rawFund.MONTHLY_YIELD),
          ytd: safeParseFloat(rawFund.YEAR_TO_DATE_YIELD),
          year1: safeParseFloat(rawFund.COMPUTED_YEARLY_RETURN) || 
                 safeParseFloat(rawFund.YEAR_TO_DATE_YIELD),
          year3: safeParseFloat(rawFund.YIELD_TRAILING_3_YRS),
          year5: safeParseFloat(rawFund.YIELD_TRAILING_5_YRS),
          avgAnnual3Years: safeParseFloat(rawFund.AVG_ANNUAL_YIELD_TRAILING_3YRS),
          avgAnnual5Years: safeParseFloat(rawFund.AVG_ANNUAL_YIELD_TRAILING_5YRS)
        },
        
        fees: {
          management: safeParseFloat(rawFund.AVG_ANNUAL_MANAGEMENT_FEE),
          deposit: safeParseFloat(rawFund.AVG_DEPOSIT_FEE)
        },
        
        performance: {
          standardDeviation: safeParseFloat(rawFund.STANDARD_DEVIATION),
          alpha: safeParseFloat(rawFund.ALPHA),
          sharpeRatio: safeParseFloat(rawFund.SHARPE_RATIO),
          liquidAssetsPercent: safeParseFloat(rawFund.LIQUID_ASSETS_PERCENT)
        },
        
        financialData: {
          totalAssets: (safeParseFloat(rawFund.TOTAL_ASSETS) || 0) * 1000000,
          deposits: safeParseFloat(rawFund.DEPOSITS) || 0,
          withdrawals: safeParseFloat(rawFund.WITHDRAWLS) || 0,
          netMonthlyDeposits: safeParseFloat(rawFund.NET_MONTHLY_DEPOSITS) || 0
        },
        
        exposures: {
          stocks: safeParseFloat(rawFund.STOCK_MARKET_EXPOSURE),
          foreign: safeParseFloat(rawFund.FOREIGN_EXPOSURE),
          currency: safeParseFloat(rawFund.FOREIGN_CURRENCY_EXPOSURE)
        },
        
        fundInfo: {
          inceptionDate: rawFund.INCEPTION_DATE || null,
          targetPopulation: rawFund.TARGET_POPULATION || 'כלל האוכלוסיה',
          specialization: rawFund.SPECIALIZATION || '',
          subSpecialization: rawFund.SUB_SPECIALIZATION || '',
          controllingCorp: rawFund.CONTROLLING_CORPORATION || '',
          managingCorp: rawFund.MANAGING_CORPORATION || ''
        },
        
        technical: {
          reportPeriod: rawFund.REPORT_PERIOD?.toString() || '',
          lastUpdate: new Date().toISOString(),
          managingCorpLegalId: rawFund.MANAGING_CORPORATION_LEGAL_ID || ''
        }
      };
    }
  }

  /**
   * 🕒 פורמט זמן יחסי
   */
  formatRelativeTime(date) {
    if (!date) return 'לא ידוע';
    
    try {
      const now = new Date();
      const past = new Date(date);
      const diffMs = now - past;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffHours < 1) return 'עכשיו';
      if (diffHours < 24) return `לפני ${diffHours} שעות`;
      if (diffDays < 7) return `לפני ${diffDays} ימים`;
      return `לפני ${Math.floor(diffDays / 7)} שבועות`;
    } catch (error) {
      console.warn('Error formatting date:', error);
      return 'לא ידוע';
    }
  }

  /**
   * 🔍 בדיקת מצב חיבור
   */
  async checkConnection() {
    try {
      // בדיקת מצב Authentication
      const user = auth.currentUser;
      const isAuthenticated = !!user;
      
      // בדיקת מצב Cloud Functions
      const response = await fetch(`${this.baseUrl}/getGamalNetData`, {
        method: 'GET',
        timeout: 5000
      });
      
      const functionsHealthy = response.ok;
      
      return {
        authenticated: isAuthenticated,
        functionsHealthy: functionsHealthy,
        userId: user?.uid || null,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Connection check failed:', error);
      return {
        authenticated: !!auth.currentUser,
        functionsHealthy: false,
        userId: auth.currentUser?.uid || null,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
}

// יצירת instance יחיד
const adminAPI = new AdminAPI();

export default adminAPI;